/* eslint-disable complexity */
import React from 'react';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestCycle } from '@qality/common-model';

import { TestCyclesOverviewRow } from './TestCyclesOverviewTable.row';

type Columns =
  | 'testCycle'
  | 'project'
  | 'version'
  | 'status'
  | 'state'
  | 'dueDate'
  | 'created'
  | 'actions';

const ColumnsWidths = createColumnsWidths<Columns>({
  testCycle: createColumnWidths('600px', '240px', '400px'),
  project: createColumnWidths('auto', '230px'),
  version: createColumnWidths('100px'),
  status: createColumnWidths('180px', '120px'),
  state: createColumnWidths('auto', '80px'),
  created: createColumnWidths('auto', '80px'),
  dueDate: createColumnWidths('auto', '80px'),
  actions: createColumnWidths('100px'),
});

export type TestCyclesOverviewTableViewProps = {
  hasFilters: boolean;
  testCycles: TestCycle[];
  onDelete: (testCycle: TestCycle) => void;
};

export const TestCyclesOverviewTableView: React.FC<TestCyclesOverviewTableViewProps> =
  (props: TestCyclesOverviewTableViewProps) => {
    const { testCycles } = props;
    let infoMessage = null;

    /**
     * WHEN filters are set but nothing found
     */
    if (testCycles.length <= 0) {
      infoMessage = props.hasFilters
        ? "We couldn't find cycles with current parameters."
        : "No test cycles have been created yet, or you don't have permission to view them.";
    }

    const hasActionsPermissions = testCycles.some(
      (testCycle) => testCycle.canEdit
    );

    return (
      <Table data-testid='TestCyclesOverviewTableView-Table' addStickyHeader>
        <Table.Head>
          <Table.Tr>
            <Table.Th widths={ColumnsWidths.testCycle}>Test Cycle</Table.Th>
            <Table.Th widths={ColumnsWidths.project}>Project</Table.Th>
            <Table.Th widths={ColumnsWidths.version}>Version</Table.Th>
            <Table.Th widths={ColumnsWidths.status}>Status</Table.Th>
            {FEATURE_FLAG.isPremium && (
              <Table.Th widths={ColumnsWidths.state}>State</Table.Th>
            )}
            <Table.Th widths={ColumnsWidths.created}>Created</Table.Th>
            <Table.Th widths={ColumnsWidths.dueDate}>Due date</Table.Th>
            {hasActionsPermissions && (
              <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
            )}
          </Table.Tr>
        </Table.Head>

        <Table.Body>
          {infoMessage && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Flex justifyCenter paddingSize='medium'>
                  <Text>{infoMessage}</Text>
                </Flex>
              </Table.Td>
            </Table.Tr>
          )}
          {!infoMessage &&
            testCycles.map((testCycle, index) => {
              return (
                <TestCyclesOverviewRow
                  key={`${testCycle.id}-${index}`}
                  testCycle={testCycle}
                  rowNumber={index}
                  hasActionsPermissions={hasActionsPermissions}
                  onDelete={props.onDelete}
                />
              );
            })}
        </Table.Body>
      </Table>
    );
  };
